import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import { CourseDetail, CourseLayout, CourseModule, CourseSectionWithModules } from '../../types/course';
import { useImageUrl } from '../../services/uploads';
import PreviewWelcomeHero, { BackPill } from '../programs/preview/PreviewWelcomeHero';
import PreviewTileGrid from '../programs/preview/PreviewTileGrid';
import PreviewTile from '../programs/preview/PreviewTile';
import { PreviewList, default as PreviewListItem } from '../programs/preview/PreviewList';
import CoursePreviewPlayer from './CoursePreviewPlayer';

/**
 * The full "view as learner" preview. Mirrors the learner course journey screen
 * for screen — welcome hero → sections grid → modules-in-section grid → the
 * module player — reusing the exact learner presentational components
 * (PreviewWelcomeHero / PreviewTile / PreviewTileGrid) fed with admin course
 * data. It's a free-roam rehearsal: every tile is open (the preview has no
 * learner progress to gate on) and no progress is written.
 */

type View =
  | { screen: 'overview' }
  | { screen: 'sections' }
  | { screen: 'section'; sectionId: string }
  | { screen: 'module'; moduleId: string };

interface CoursePreviewExperienceProps {
  course: CourseDetail;
  /** Leave the preview entirely (page back / overlay close). */
  onExit: () => void;
}

const byOrder = <T extends { order_index?: number }>(a: T, b: T) =>
  (a.order_index ?? 0) - (b.order_index ?? 0);

const CoursePreviewExperience: React.FC<CoursePreviewExperienceProps> = ({ course, onExit }) => {
  const [view, setView] = useState<View>({ screen: 'overview' });

  const sections = useMemo(
    () => (course.sections ?? []).slice().sort(byOrder),
    [course]
  );

  if (view.screen === 'sections') {
    return (
      <SectionsScreen
        title={course.title ?? 'Course'}
        sections={sections}
        layout={course.layout}
        onBack={() => setView({ screen: 'overview' })}
        onOpenSection={(sectionId) => setView({ screen: 'section', sectionId })}
      />
    );
  }

  if (view.screen === 'section') {
    const index = sections.findIndex((s) => s.id === view.sectionId);
    const section = index >= 0 ? sections[index] : null;
    if (section) {
      return (
        <SectionModulesScreen
          section={section}
          index={index}
          layout={course.layout}
          onBack={() => setView({ screen: 'sections' })}
          onOpenModule={(moduleId) => setView({ screen: 'module', moduleId })}
        />
      );
    }
    // Section vanished (shouldn't happen) — fall back to the sections grid.
    return (
      <SectionsScreen
        title={course.title ?? 'Course'}
        sections={sections}
        layout={course.layout}
        onBack={() => setView({ screen: 'overview' })}
        onOpenSection={(sectionId) => setView({ screen: 'section', sectionId })}
      />
    );
  }

  if (view.screen === 'module') {
    return (
      <CoursePreviewPlayer
        key={view.moduleId}
        course={course}
        urlSync={false}
        startModuleId={view.moduleId}
        onBackToSection={(sectionId) => setView({ screen: 'section', sectionId })}
        onExit={() => setView({ screen: 'overview' })}
      />
    );
  }

  // Overview (welcome) — the learner's landing screen.
  return <OverviewScreen course={course} onStart={() => setView({ screen: 'sections' })} onExit={onExit} />;
};

// ─── Overview (welcome hero) ─────────────────────────────────────────────────

const OverviewScreen: React.FC<{
  course: CourseDetail;
  onStart: () => void;
  onExit: () => void;
}> = ({ course, onStart, onExit }) => {
  const [coverUrl] = useImageUrl(course.image_url ?? null);
  const durationLabel =
    course.estimated_duration_minutes != null
      ? `${course.estimated_duration_minutes} min`
      : undefined;
  const hasModules = (course.sections ?? []).some((s) => (s.modules ?? []).length > 0);

  return (
    <motion.div {...slideUp} className="w-[95%] sm:w-[90%] mx-auto py-6 space-y-6">
      <PreviewWelcomeHero
        coverUrl={coverUrl ?? undefined}
        title={course.title ?? 'Course'}
        description={course.subtitle ?? undefined}
        durationLabel={durationLabel}
        compact
        backAction={{ label: 'Exit preview', onClick: onExit }}
        cta={hasModules ? { label: 'Start preview', onClick: onStart } : undefined}
      />
      {!hasModules && (
        <div className="card p-6 text-center" data-testid="preview-no-content">
          <p className="text-sm font-semibold text-text-primary">
            Nothing to preview yet
          </p>
          <p className="text-sm text-text-secondary mt-1">
            This course has no content — add sections and modules in the
            editor, then come back to preview it as a learner.
          </p>
        </div>
      )}
    </motion.div>
  );
};

// ─── Sections grid ───────────────────────────────────────────────────────────

const SectionsScreen: React.FC<{
  title: string;
  sections: CourseSectionWithModules[];
  layout: CourseLayout;
  onBack: () => void;
  onOpenSection: (sectionId: string) => void;
}> = ({ title, sections, layout, onBack, onOpenSection }) => (
  <motion.div
    {...slideUp}
    className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 sm:space-y-8"
  >
    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-primary-500 leading-[1.05] tracking-tight px-1">
      {title}
    </h1>
    <div className="px-1">
      <BackPill label="Go back" onClick={onBack} />
    </div>
    {sections.length === 0 ? (
      <div className="card p-8 text-center text-sm text-text-secondary">
        This course doesn&apos;t have any sections yet.
      </div>
    ) : layout === 'list' ? (
      <PreviewList>
        {sections.map((section, i) => (
          <PreviewListItem
            key={section.id}
            imageUrl={section.image_url ?? null}
            kicker={`Section ${i + 1}`}
            index={i}
            title={section.title || `Section ${i + 1}`}
            entranceDelay={i * 0.04}
            onClick={() => onOpenSection(section.id)}
          />
        ))}
      </PreviewList>
    ) : (
      <PreviewTileGrid maxColumns={4}>
        {sections.map((section, i) => (
          <SectionTile
            key={section.id}
            section={section}
            index={i}
            onOpen={() => onOpenSection(section.id)}
          />
        ))}
      </PreviewTileGrid>
    )}
  </motion.div>
);

const SectionTile: React.FC<{
  section: CourseSectionWithModules;
  index: number;
  onOpen: () => void;
}> = ({ section, index, onOpen }) => {
  const [coverUrl] = useImageUrl(section.image_url ?? null);
  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={section.title || `Section ${index + 1}`}
      state="open"
      lockedScope="section"
      entranceDelay={index * 0.05}
      onClick={onOpen}
      aspectClassName="aspect-[4/5]"
    />
  );
};

// ─── Modules-in-section grid ─────────────────────────────────────────────────

const SectionModulesScreen: React.FC<{
  section: CourseSectionWithModules;
  index: number;
  layout: CourseLayout;
  onBack: () => void;
  onOpenModule: (moduleId: string) => void;
}> = ({ section, index, layout, onBack, onOpenModule }) => {
  const modules = useMemo(
    () => (section.modules ?? []).slice().sort(byOrder),
    [section]
  );

  return (
    <motion.div
      {...slideUp}
      className="w-[95%] sm:w-[90%] mx-auto py-6 sm:py-10 space-y-6 sm:space-y-8"
    >
      <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05] tracking-tight px-1">
        <span className="text-primary-500 uppercase tracking-tight">Section {index + 1}:</span>{' '}
        <span className="text-text-primary">{section.title || `Section ${index + 1}`}</span>
      </h1>
      <div className="px-1">
        <BackPill label="Go back" onClick={onBack} />
      </div>
      {modules.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-secondary">
          This section doesn&apos;t have any modules yet.
        </div>
      ) : layout === 'list' ? (
        <PreviewList>
          {modules.map((module, i) => (
            <PreviewListItem
              key={module.id}
              imageUrl={module.image_url ?? null}
              kicker={`Content ${i + 1}`}
              index={i}
              title={module.title || `Module ${i + 1}`}
              entranceDelay={i * 0.04}
              onClick={() => onOpenModule(module.id)}
            />
          ))}
        </PreviewList>
      ) : (
        <PreviewTileGrid maxColumns={4}>
          {modules.map((module, i) => (
            <ModuleTile
              key={module.id}
              module={module}
              index={i}
              onOpen={() => onOpenModule(module.id)}
            />
          ))}
        </PreviewTileGrid>
      )}
    </motion.div>
  );
};

const ModuleTile: React.FC<{
  module: CourseModule;
  index: number;
  onOpen: () => void;
}> = ({ module, index, onOpen }) => {
  const [coverUrl] = useImageUrl(module.image_url ?? null);
  return (
    <PreviewTile
      imageUrl={coverUrl ?? undefined}
      label={module.title || `Module ${index + 1}`}
      state="open"
      lockedScope="content"
      entranceDelay={index * 0.05}
      onClick={onOpen}
      aspectClassName="aspect-[4/5]"
    />
  );
};

export default CoursePreviewExperience;
